import { useMemo, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { useQuery } from 'react-query'

import IconChevronRight from '@/assets/icons/icon-chevron-right.svg'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { FormSelect } from '@/components/ui/form/FormSelect.tsx'
import { Separator } from '@/components/ui/separator'
import { useToast } from '@/components/ui/toast.tsx'
import type { DialogProps } from '@/dialogs/types'
import { useListMutations } from '@/modules/tiktok-research/hooks/useListMutations.ts'
import { getLists } from '@/modules/tiktok-research/service'
import type { AddToListRequest } from '@/modules/tiktok-research/types.ts'
import { getListsOptions } from '@/modules/tiktok-research/utils.tsx'
import { CreateListFormFields } from '@/modules/tiktok-research/videos/ui/create-list-form/CreateListFormFields.tsx'
import type { CreateListFormValues } from '@/modules/tiktok-research/videos/ui/create-list-form/validations.ts'
import {
  defaultList,
  listCreationFormSchema,
} from '@/modules/tiktok-research/videos/ui/create-list-form/validations.ts'
import { handleError } from '@/utils/error.ts'

import {
  type ListSelectionFormValues,
  listSelectionFormSchema,
} from './validation.ts'

interface Props extends DialogProps {
  selectedItems: AddToListRequest['itemIds']
  onAdd: () => void
  title: string
  description: string
  mediaType: AddToListRequest['mediaType']
}

const creationFormId = 'creation-form'
const selectionFormId = 'selection-form'

export const AddToTikTokListDialog = ({
  isOpen,
  onClose,
  selectedItems,
  onAdd,
  title,
  description,
  mediaType,
}: Props) => {
  const { addToList, createList } = useListMutations()
  const [showCreateListForm, setShowCreateListForm] = useState(false)
  const listsQuery = useQuery('lists', getLists, {
    onError: (error) => {
      handleError(error, 'Failed to get lists', listsQuery.refetch)
    },
  })
  const toast = useToast()

  const listSelectionForm = useForm<ListSelectionFormValues>({
    resolver: zodResolver(listSelectionFormSchema),
    mode: 'onChange',
  })
  const listCreationForm = useForm<CreateListFormValues>({
    defaultValues: defaultList,
    resolver: zodResolver(listCreationFormSchema),
    mode: 'onChange',
  })

  const listsOptions = useMemo(() => {
    return getListsOptions(listsQuery.data ?? [])
  }, [listsQuery.data])

  const addItemsToList = (
    listId: AddToListRequest['listId'],
    itemIds: AddToListRequest['itemIds'],
  ) => {
    return addToList
      .mutateAsync({ itemIds, listId, mediaType })
      .then(() => {
        listSelectionForm.reset()
        onClose()
        onAdd()
        toast.success('Selected items added to list')
      })
      .catch((error) => {
        handleError(error, 'Something went wrong while adding medias to list')
      })
  }

  const onSubmitSelectionForm = ({ list }: ListSelectionFormValues) =>
    addItemsToList(Number(list), selectedItems)

  const onSubmitCreationForm = (values: CreateListFormValues) => {
    return createList
      .mutateAsync(values)
      .then((list) => addItemsToList(list.id, selectedItems))
      .catch((error) => {
        handleError(error, 'Something went wrong while creating list')
      })
  }

  const currentForm = showCreateListForm ? listCreationForm : listSelectionForm

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      panelClassName="overflow-visible"
      bigDialog
    >
      <Dialog.Header>
        <Dialog.Title className="text-center text-3xl font-semibold leading-9">
          {title}
        </Dialog.Title>
        <Dialog.Description className="text-center">
          {description}
        </Dialog.Description>
      </Dialog.Header>
      <Dialog.Description>
        <div className="mt-6">
          {showCreateListForm ? (
            <form
              onSubmit={listCreationForm.handleSubmit(onSubmitCreationForm)}
              className="space-y-6"
              id={creationFormId}
            >
              <FormProvider {...listCreationForm}>
                <CreateListFormFields />
              </FormProvider>
            </form>
          ) : (
            <div className="space-y-6">
              <form
                id={selectionFormId}
                onSubmit={listSelectionForm.handleSubmit(onSubmitSelectionForm)}
              >
                <FormProvider {...listSelectionForm}>
                  <FormSelect
                    label="Add to existing List"
                    name="list"
                    placeholder="Select List"
                    options={listsOptions}
                    disabled={listsQuery.isLoading}
                  />
                </FormProvider>
              </form>
              <div className="w-max-full">
                <div className="flex items-center justify-center gap-5">
                  <Separator className="flex-1" />
                  <span className="text-text-default text-xs leading-5">
                    Or
                  </span>
                  <Separator className="flex-1" />
                </div>
              </div>
              <div className="text-center">
                <Button
                  variant="ghost"
                  onClick={() => setShowCreateListForm(true)}
                  disabled={listSelectionForm.formState.isSubmitting}
                >
                  Create New List
                  <IconChevronRight className="ml-2" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </Dialog.Description>
      <Dialog.ClosableFooter onClose={onClose}>
        <Button
          disabled={!currentForm.formState.isValid}
          isLoading={currentForm.formState.isSubmitting}
          type="submit"
          form={showCreateListForm ? creationFormId : selectionFormId}
        >
          Save
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}
